import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { validateOTP, requestOTP, register } from '../../../../services/auth';
import { useIsMobile } from '../../../../hooks/useIsMobile';

const StepVerifyWeb = ({ formData, updateFormData, goToStep, onComplete, isDoctor }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Resend OTP
  const resendMutation = useMutation(
    (data) => requestOTP(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Failed to resend verification code');
        }
      },
      onSuccess: (res) => {
        updateFormData('nonce', res.data.data.nonce);
        setCanResend(false);
        setTimer(60);
        setError('');
      },
    }
  );

  // Verify OTP and register
  const verifyMutation = useMutation(
    (data) => validateOTP(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Invalid verification code');
        }
      },
      onSuccess: () => {
        // OTP verified, now register the user
        const registrationData = {
          name: formData.fullName,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          state: formData.state,
          country: formData.country,
          profession_type_code: formData.specialization,
          scope: isDoctor ? 'doctor' : 'clinic_owner',
        };

        // Include location data for facilities
        if (!isDoctor && formData.latitude && formData.longitude) {
          registrationData.latitude = formData.latitude;
          registrationData.longitude = formData.longitude;
        }

        registerMutation.mutate(registrationData);
      },
    }
  );

  // Register user
  const registerMutation = useMutation(
    (data) => register(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Registration failed');
        }
      },
      onSuccess: () => {
        onComplete();
      },
    }
  );

  const handleOtpChange = (value, index) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e, index) => {
    const pastedData = e.nativeEvent?.text || '';
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
    }
  };

  const handleResend = () => {
    resendMutation.mutate({ email: formData.email });
  };

  const handleVerify = () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    verifyMutation.mutate({
      email: formData.email,
      otp: otpCode,
      nonce: formData.nonce,
    });
  };

  const isLoading = verifyMutation.isLoading || registerMutation.isLoading || resendMutation.isLoading;
  const otpComplete = otp.every(digit => digit !== '');

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepIcon}>✉️</Text>
        <Text style={styles.stepTitle}>Email Verification</Text>
        <Text style={styles.otpEmail}>
          We have sent an email to{' '}
          <Text style={styles.emailHighlight}>{formData.email}</Text>
        </Text>
      </View>

      {error ? (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={[styles.otpBoxes, isMobile && styles.otpBoxesMobile]}>
        {otp.map((digit, index) => (
          <TextInput
            key={index}
            ref={(ref) => (inputRefs.current[index] = ref)}
            style={[
              styles.otpInput,
              isMobile && styles.otpInputMobile,
              digit && styles.otpInputFilled,
            ]}
            value={digit}
            onChangeText={(value) => handleOtpChange(value, index)}
            onKeyPress={(e) => handleKeyDown(e, index)}
            keyboardType="number-pad"
            maxLength={1}
            editable={!isLoading}
            selectTextOnFocus
          />
        ))}
      </View>

      <Text style={styles.otpHint}>
        It may take a few seconds to receive. You may also check your spam folder for the OTP.
      </Text>

      <TouchableOpacity
        style={[styles.resendBtn, (!canResend || isLoading) && styles.resendBtnDisabled]}
        onPress={handleResend}
        disabled={!canResend || isLoading}
      >
        <Text style={styles.resendBtnText}>✉️ Resend Email Verification</Text>
      </TouchableOpacity>

      {!canResend && (
        <Text style={styles.timerText}>Please wait for {timer} seconds</Text>
      )}

      <View style={styles.btnRow}>
        <TouchableOpacity
          style={styles.btnBack}
          onPress={() => goToStep(isDoctor ? 3 : 4)}
          disabled={isLoading}
        >
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, (!otpComplete || isLoading) && styles.btnNextDisabled]}
          onPress={handleVerify}
          disabled={!otpComplete || isLoading}
        >
          <Text style={styles.btnNextText}>
            {isLoading ? 'Verifying...' : 'Verify'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stepHeader: {
    marginBottom: 32,
  },
  stepIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  otpEmail: {
    fontSize: 16,
    color: webColors.textSecondary,
  },
  emailHighlight: {
    color: webColors.primary,
    fontWeight: '600',
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  otpBoxes: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 20,
  },
  otpInput: {
    width: 52,
    height: 60,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    color: webColors.textPrimary,
  },
  otpBoxesMobile: {
    gap: 8,
  },
  otpInputMobile: {
    width: 40,
  },
  otpInputFilled: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  otpHint: {
    textAlign: 'center',
    fontSize: 14,
    color: webColors.primary,
    marginBottom: 32,
    lineHeight: 22,
  },
  resendBtn: {
    backgroundColor: webColors.backgroundAlt,
    paddingVertical: 14,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  resendBtnDisabled: {
    opacity: 0.6,
  },
  resendBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  timerText: {
    textAlign: 'center',
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 24,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  btnBack: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: webColors.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    opacity: 0.6,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default StepVerifyWeb;
